import PropTypes from 'prop-types';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import loginWithCredentials, { changePassword } from './passwordLogin';
import {
  exchangeCode,
  fetchProfile,
  readAuthorizationCode,
  refreshSession,
  stripAuthParamsFromUrl,
  verifyState,
} from './questionpro';
import { clearToken, getToken, isExpired, setToken } from './tokenStorage';

export const AuthContext = createContext(null);

// Refresh once the token is within this window of expiring, so a request that
// is already in flight cannot outlive it.
const REFRESH_SKEW_SECS = 300;

// The dedicated callback page owns the code exchange on this path (it can show
// a proper error). Anywhere else, the provider handles it - QuestionPro sends
// the user to whatever the OAuth client registration says, which may be the
// bare origin rather than a /callback path.
const CALLBACK_PATH = '/callback';

export const LOGIN_PATH = '/login';
export const CHANGE_PASSWORD_PATH = '/change-password';

function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Where to land after signing in, remembered from the route that bounced us.
  const returnToRef = useRef('/');

  // The token also lives in a ref so the callbacks below stay referentially
  // stable - useFetch depends on getAccessTokenSilently, and a new identity on
  // every render would retrigger every react-query fetcher.
  const tokenRef = useRef(null);
  const refreshRef = useRef(null);

  const signOut = useCallback(() => {
    clearToken();
    tokenRef.current = null;
    refreshRef.current = null;
    setUser(null);
  }, []);

  // Keeps the name the whole app already calls, so route guards and the navbar
  // are unchanged - it just sends users to our own sign-in page rather than to
  // an identity provider.
  const loginWithRedirect = useCallback(() => {
    const { pathname, search } = window.location;
    if (pathname !== LOGIN_PATH) {
      returnToRef.current = `${pathname}${search}`;
    }
    setAuthError(null);
    navigate(LOGIN_PATH, { replace: true });
  }, [navigate]);

  const logout = useCallback(() => {
    signOut();
    returnToRef.current = '/';
    navigate(LOGIN_PATH, { replace: true });
  }, [signOut, navigate]);

  // Adopts a freshly exchanged token and loads the profile behind it. Used by
  // the OAuth callback page and by the bootstrap effect below.
  const activateToken = useCallback(
    async (token) => {
      tokenRef.current = token;
      setToken(token);
      try {
        const profile = await fetchProfile(token);
        setUser(profile);
        return profile;
      } catch (err) {
        signOut();
        throw err;
      }
    },
    [signOut],
  );

  // Signs in with email + password and lands the user back where they started.
  // Errors propagate so the form can show them inline.
  const signIn = useCallback(
    async (email, password) => {
      const token = await loginWithCredentials(email, password);
      await activateToken(token);
      setAuthError(null);
      navigate(returnToRef.current || '/', { replace: true });
      returnToRef.current = '/';
    },
    [activateToken, navigate],
  );

  // Swaps out a temporary password. The API refuses everything else until this
  // succeeds, so there is nowhere else to go from the change-password screen.
  const completePasswordChange = useCallback(
    async (currentPassword, newPassword) => {
      const token = await changePassword(tokenRef.current, currentPassword, newPassword);
      await activateToken(token);
      navigate(returnToRef.current || '/', { replace: true });
      returnToRef.current = '/';
    },
    [activateToken, navigate],
  );

  const getAccessTokenSilently = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) {
      throw new Error('Not authenticated');
    }
    if (!isExpired(token, REFRESH_SKEW_SECS)) {
      return token;
    }
    // Collapse concurrent callers onto a single refresh request.
    if (!refreshRef.current) {
      refreshRef.current = refreshSession(token)
        .then((next) => {
          tokenRef.current = next;
          setToken(next);
          return next;
        })
        .catch((err) => {
          signOut();
          throw err;
        })
        .finally(() => {
          refreshRef.current = null;
        });
    }
    return refreshRef.current;
  }, [signOut]);

  // Called by useFetch when the API rejects our credentials mid-session.
  const handleAuthFailure = useCallback(() => {
    signOut();
    loginWithRedirect();
  }, [signOut, loginWithRedirect]);

  const bootstrapped = useRef(false);

  useEffect(() => {
    // The OAuth code is single-use; never let a re-render spend it twice.
    if (bootstrapped.current) {
      return;
    }
    bootstrapped.current = true;

    const { code, state } = readAuthorizationCode();
    if (code && window.location.pathname !== CALLBACK_PATH) {
      stripAuthParamsFromUrl();
      if (!verifyState(state)) {
        setAuthError('Login state did not match. Please try signing in again.');
        setIsLoading(false);
        return;
      }
      exchangeCode(code)
        .then((token) => activateToken(token))
        .catch((err) => setAuthError(err.message))
        .finally(() => setIsLoading(false));
      return;
    }

    const stored = getToken();
    if (!stored || isExpired(stored)) {
      clearToken();
      setIsLoading(false);
      return;
    }
    activateToken(stored)
      .catch(() => {
        // activateToken already cleared the session; fall through as anonymous.
      })
      .finally(() => setIsLoading(false));
  }, [activateToken]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      authError,
      mustChangePassword: Boolean(user?.must_change_password),
      loginWithRedirect,
      signIn,
      completePasswordChange,
      logout,
      getAccessTokenSilently,
      completeLogin: activateToken,
      handleAuthFailure,
    }),
    [
      user,
      isLoading,
      authError,
      loginWithRedirect,
      signIn,
      completePasswordChange,
      logout,
      getAccessTokenSilently,
      activateToken,
      handleAuthFailure,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };

export default AuthProvider;
